// libs
import { Amplify, Auth, API } from "aws-amplify";

// defs
import { CognitoUser } from "amazon-cognito-identity-js";
import * as graphQlQueries from "@utils/appsync/schema/queries";
import * as graphQlMutations from "@utils/appsync/schema/mutations";
import * as graphQlSubscriptions from "@utils/appsync/schema/subscriptions";

// Service configuration
interface ServiceConfig {
    graphqlEndpoint: string;
    region: string;
    authenticationType: string;
    apiKey?: string;
}

// Store configurations for different services
let serviceConfigurations: { [key: string]: ServiceConfig } = {};

const getClient = (serviceName?: string): typeof API => {
    if (serviceName && serviceConfigurations[serviceName]) {
        return getOverridenClient(serviceName);
    }
    return API;
};

const getOverridenClient = (serviceName: string): typeof API => {
    const customServiceConfig = serviceConfigurations[serviceName];
    // Create a custom client that uses the Custom Service endpoint
    return {
        ...API,
        graphql: async (params: any) => {
            // Store current configuration values

            try {
                // Temporarily reconfigure Amplify for the Custom Service
                Amplify.configure({
                    ...(Amplify as any)._config,
                    aws_appsync_graphqlEndpoint: customServiceConfig.graphqlEndpoint,
                    aws_appsync_authenticationType: customServiceConfig.authenticationType,
                    aws_appsync_region: customServiceConfig.region,
                });

                // Make the GraphQL request
                const result = await API.graphql(params);

                return result;
            } finally {
                // Always restore the original configuration
                Amplify.configure({
                    ...(Amplify as any)._config,
                    aws_appsync_graphqlEndpoint: serviceConfigurations["default"].graphqlEndpoint,
                    aws_appsync_authenticationType: serviceConfigurations["default"].authenticationType,
                    aws_appsync_region: serviceConfigurations["default"].region,
                    aws_appsync_apiKey: serviceConfigurations["default"].apiKey,
                });
            }
        },
    } as typeof API;
};

const configureAmplify = async (settingsData: any): Promise<CognitoUser> => {
    //@ts-ignore
    const windowLocation = window.location;

    // Store service configurations for different endpoints
    serviceConfigurations = {
        // Default service configuration
        default: {
            graphqlEndpoint: settingsData?.appsync?.graphqlEndpoint,
            region: settingsData?.appsync?.region,
            authenticationType: settingsData?.appsync?.authenticationType,
            apiKey: settingsData?.appsync?.apiKey,
        },
        // DataInputTool service configuration
        // Must be configured via appsyncServices in settings
        dataInputTool: {
            graphqlEndpoint: settingsData?.appsyncServices?.dataInputTool?.graphqlEndpoint || settingsData?.appsync?.graphqlEndpoint,
            region: settingsData?.appsyncServices?.dataInputTool?.region || settingsData?.appsync?.region,
            authenticationType:
                settingsData?.appsyncServices?.dataInputTool?.authenticationType || settingsData?.appsync?.authenticationType,
            apiKey: settingsData?.appsyncServices?.dataInputTool?.apiKey || settingsData?.appsync?.apiKey,
        },
    };

    Amplify.configure({
        aws_project_region: settingsData?.appsync?.region,
        aws_appsync_region: settingsData?.appsync?.region,
        aws_appsync_graphqlEndpoint: settingsData?.appsync?.graphqlEndpoint,
        aws_appsync_authenticationType: settingsData?.appsync?.authenticationType,
        aws_appsync_apiKey: settingsData?.appsync?.apiKey,
        Auth: {
            identityPoolId: settingsData?.cognito?.identityPoolId,
            userPoolId: settingsData?.cognito?.userPoolId,
            userPoolWebClientId: settingsData?.cognito?.userPoolClientId,
            region: settingsData?.cognito?.region,
            oauth: {
                domain: settingsData?.cognito?.loginDomain,
                scope: ["openid"],
                responseType: "code",
                redirectSignIn: "https://" + windowLocation.host,
                redirectSignOut: "https://" + windowLocation.host,
            },
            /*"Cognito": {
                "identityPoolId": settingsData?.cognito?.identityPoolId,
                "userPoolId": settingsData?.cognito?.userPoolId,
                "userPoolClientId": settingsData?.cognito?.userPoolClientId,
                "region": settingsData?.cognito?.region,
                "loginWith": {
                    "oauth": {
                        "domain": settingsData?.cognito?.loginDomain,
                        "scope": ["openid"],
                        "responseType": "code",
                        "providers": [
                            {
                                "custom": settingsData?.cognito?.providerName
                            }
                        ]
                    },
                    "username": "true"
                }
            }*/
        },
    });
    return await getCurrentUser(settingsData?.cognito?.providerName);
};

const getCurrentUser = async (providerName: string): Promise<CognitoUser> => {
    try {
        return await Auth.currentAuthenticatedUser();
    } catch (e) {
        await Auth.federatedSignIn({
            customProvider: providerName,
        });
    }
};
const fetchCredentials = (): typeof Auth.currentCredentials => {
    return Auth.currentCredentials;
};

export { configureAmplify, getCurrentUser, fetchCredentials, getClient, graphQlQueries, graphQlMutations, graphQlSubscriptions };
